/**
 * Calculatie-parameters — DTO's + Zod-schemas.
 *
 * Conform docs/specs/20-calculatie-offerte-tool.md §2.1 + §3.1. Bedrijfsbrede
 * parameters (werkgeverslasten, overhead, declarabele uren, brandstof, risico-
 * opslag) die de kostprijsberekening aansturen. Beheerd door ADMIN via
 * `/instellingen/calculatie`.
 *
 * Waarden komen als `number` over de API-grens (de DB-kolom is Decimal(10,4));
 * de API serialiseert Decimal → number en valideert met de schemas hieronder.
 */

import { z } from 'zod'

/// Vaste set parameter-keys uit de Excel "Parameters"-sheet (spec 20 §2.1).
/// Bewaard als const-tuple zodat de seed + UI dezelfde keys delen. Vrije keys
/// blijven mogelijk in de DB (kolom is `String`), maar dit zijn de bekende.
export const CALCULATION_PARAMETER_KEYS = [
  'werkgeverslasten_pct',
  'vakantiegeld_pct',
  'pensioen_pct',
  'opleiding_per_fte',
  'ziekte_pct',
  'overhead_pct',
  'declarabele_uren_jaar',
  'werkdagen_jaar',
  'brandstof_per_liter',
  'gem_verbruik_liter_100km',
  'risico_opslag_pct',
  'standaard_marge_materiaal_pct',
  // Batch-9 #71 — kostprijs ↔ opbrengst vergelijk-weergave (instelbaar, niet hardcoden).
  'km_tarief_opbrengst',
  'verlof_uren_jaar',
  'weken_per_jaar',
  // Batch-9-vervolg V6 (PO #8) — organisatiebrede marge-defaults (per klant
  // overschrijfbaar via Client.minimumMargePct). VOORBEELD-waarden tot de PO de
  // echte minimum-/doelmarge levert.
  'org_minimum_marge_pct',
  'org_doel_marge_pct',
] as const

export type CalculationParameterKey = (typeof CALCULATION_PARAMETER_KEYS)[number]

/// Bekende parameter-groepen voor de UI-gruppering (spec 20 §2.1 tabel).
export const CALCULATION_PARAMETER_GROUPS = [
  'loonkosten',
  'capaciteit',
  'voertuig',
  'project',
  // V6 (PO #8) — organisatiebrede marge-defaults.
  'marge',
  'algemeen',
] as const

export type CalculationParameterGroup = (typeof CALCULATION_PARAMETER_GROUPS)[number]

export interface CalculationParameterDTO {
  id: string
  key: string
  label: string
  value: number
  unit: string | null
  description: string | null
  group: string
  sortOrder: number
  updatedAt: string
  updatedBy: string | null
}

/// Parameters gegroepeerd per `group` voor `GET /calculation-parameters`.
export interface CalculationParameterGroupDTO {
  group: string
  parameters: CalculationParameterDTO[]
}

/// `PATCH /calculation-parameters/:id` — alleen de waarde is bewerkbaar
/// (ADMIN-only). Niet-negatief; percentages/bedragen/uren zijn nooit negatief.
export const calculationParameterUpdateSchema = z.object({
  value: z.number().min(0),
})

export type CalculationParameterUpdateInput = z.infer<typeof calculationParameterUpdateSchema>
